import { Component, OnDestroy, OnInit, signal, HostListener, inject, ChangeDetectorRef } from '@angular/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { CommonModule } from '@angular/common';
import { AuthService } from './services/auth.service';
import { HeaderComponent } from './shared/header/header.component';
import { MatIconModule } from '@angular/material/icon';
import { RatingFeedbackComponent } from './components/rating-feedback/rating-feedback.component';
import { AlertContainerComponent } from './components/alert-container/alert-container.component';
import { RouterOutlet, Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { GlobalLoadingComponent } from './components/global-loading/global-loading.component';
import { LoadingService } from './services/loading.service';
import { AuthComponent } from "./auth/auth.component";


@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.css',
  standalone: true,
  imports: [
    MatSlideToggleModule,
    MatIconModule,
    HeaderComponent,
    SidebarComponent,
    CommonModule,
    RatingFeedbackComponent,
    AlertContainerComponent,
    RouterOutlet,
    GlobalLoadingComponent,
    AuthComponent
  ],
  providers: [
    AuthService
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Home';
  isAuthenticated = signal<boolean>(false);
  authStatus = 'No Authenticated';
  carrega_sidebar = false;

  private readonly destroying$ = new Subject<void>();
  profile: any;

  readonly sidebarCollapsed = signal<boolean>(false);

  private readonly oidcService = inject(OidcSecurityService);
  private readonly authService = inject(AuthService);
  private readonly loadingService = inject(LoadingService);
  private readonly router = inject(Router);

  constructor(
    private cdr: ChangeDetectorRef
  ) {
    this.setupGlobalRouteInterception();
  }

  async ngOnInit() {
    this.removeSplashScreen();

    this.authService.init();

    // Verifica o retorno do redirect (callback) e atualiza o estado
    this.oidcService.checkAuth()
      .pipe(takeUntil(this.destroying$))
      .subscribe(({ isAuthenticated, userData }) => {
        this.isAuthenticated.set(isAuthenticated);
        this.authStatus = isAuthenticated ? 'Authenticated' : 'No Authenticated';

        if (isAuthenticated && userData) {
          this.profile = {
            name: userData.name,
            email: userData.email ?? userData.preferred_username
          };
        }

        this.cdr.detectChanges();
      });

    // Monitora mudanças de autenticação ao longo da sessão
    this.oidcService.isAuthenticated$
      .pipe(takeUntil(this.destroying$))
      .subscribe(({ isAuthenticated }) => {
        this.isAuthenticated.set(isAuthenticated);
        this.authStatus = isAuthenticated ? 'Authenticated' : 'No Authenticated';
        this.cdr.detectChanges();
      });

    // Estado inicial do sidebar
    if (typeof window !== 'undefined') {
      this.sidebarCollapsed.set(window.innerWidth < 1200);
    }
  }

  login() {
    this.oidcService.authorize();
  }

  logout() {
    this.oidcService.logoff()
      .pipe(takeUntil(this.destroying$))
      .subscribe();
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update(value => !value);
  }

  private removeSplashScreen(): void {
    console.log('🚀 AppComponent: Removendo splash screen...');
    setTimeout(() => {
      const splashScreen = document.getElementById('splash-screen');
      if (splashScreen) {
        console.log('✅ AppComponent: Splash screen removido');
        splashScreen.style.opacity = '0';
        splashScreen.style.transition = 'opacity 0.5s ease-out';
        setTimeout(() => {
          splashScreen.remove();
        }, 500);
      } else {
        console.log('⚠️ AppComponent: Splash screen não encontrado');
      }
    }, 100);
  }

  private setupGlobalRouteInterception(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this.destroying$)
      )
      .subscribe((event: NavigationStart) => {
        if (event.url === this.router.url) return;
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        this.loadingService.hide();
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationCancel),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        this.loadingService.hide();
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationError),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        this.loadingService.hide();
      });
  }

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }

  private autoCollapsed = false;

  @HostListener('window:resize', [])
  onResize(): void {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 1200 && !this.sidebarCollapsed()) {
        this.sidebarCollapsed.set(true);
        this.autoCollapsed = true;
      }
      if (width >= 1200 && this.autoCollapsed) {
        this.autoCollapsed = false;
      }
    }
  }
}
